import { Info, Sparkles, AlertTriangle } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { useTranslation, Trans } from "react-i18next";
import DOMPurify from "dompurify";
import ComponentCard from "../../../../../shared/ui/common/ComponentCard";
import Button from "../../../../../shared/ui/button/Button";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../../../../../shared/ui/table";

import { SessionAnalysis, TriggeredAlert } from "../../../domain/Session";
import { useSubscription } from "../../../../subscription";

interface SessionAIAnalysisFormProps {
  analysis?: SessionAnalysis | null;
  onAnalyze?: () => void;
  loading?: boolean;
  sessionId?: string | null;
}

export default function SessionAddAIAnalysisForm({
  analysis,
  onAnalyze,
  loading = false,
  sessionId,
}: SessionAIAnalysisFormProps) {
  const { t } = useTranslation();
  const { isPremium } = useSubscription();
  const navigate = useNavigate();
  const sessionDestination = sessionId ? `/session/${sessionId}` : "/patients";
  return (
    <ComponentCard
      title={
        <div className="flex justify-between items-center w-full">
          <span className="font-semibold text-lg flex items-center gap-2">
            <span className="text-content-primary">{t("session.aiInsightsTitle")}</span>
          </span>
        </div>
      }
    >
      <div className="space-y-6">
        {/* AI Frame */}
        <div className="
          relative p-[1px] rounded-2xl mb-6 
          bg-gray-200 dark:bg-white/10
        ">
          <div className="rounded-2xl bg-surface-raised p-4 flex gap-3 items-start">
            <Info className="w-4 h-4 text-accent-text mt-0.5" />

            <p className="text-sm text-content-secondary leading-snug">
              <Trans i18nKey="session.aiInsightsDesc">
                Analyze your session notes with <strong className="text-accent-text">Alchely</strong> to get
                relevant insights, related sessions, academic articles, and
                personalized recommendations.
              </Trans>
            </p>
          </div>
        </div>

        {/* Si hay resultados */}
        {analysis && (
          <>

            {/* Triggered Alerts */}
            {isPremium && <TriggeredAlertsList data={analysis.triggered_alerts || []} />}

            {/* Summary */}
            <Summary
              main_topic={analysis.main_topic}
              emotional_status={analysis.emotional_status}
              relevant_behaviors={analysis.relevant_behaviors}
              therapeutic_advances={analysis.therapeutic_advances}
              risks={analysis.risks}
              agreements={analysis.agreements}
            />



            {/* Tables */}
            <RelatedSessionsTable data={analysis.related_sessions || []} />
            <AcademicArticlesTable data={analysis.academic_articles || []} />
            <RecommendationsTable data={analysis.recommendations || []} />
          </>
        )}

        {/* Botones */}
        {!analysis ? (
          <div className="flex justify-center pt-4 gap-2">
            <Button
              onClick={onAnalyze}
              className="ai-spectrum-fill"
              loading={loading}
              startIcon={<Sparkles className="w-4 h-4" />}
            >
              {loading ? t("session.analyzing") : t("session.runAnalysis")}
            </Button>
            <Button
              variant="outline"
              className="w-1/3"
              size="sm"
              onClick={() => navigate(sessionDestination)}
            >
              {t("session.maybeLater")}
            </Button>
          </div>
        ) : (
          <div className="flex justify-center pt-4 gap-2">
            <Button
              className="w-1/3"
              size="sm"
              onClick={() => navigate(sessionDestination)}
            >
              {t("session.finish")}
            </Button>
          </div>
        )}
      </div>
    </ComponentCard>
  );
}

/* -------------------------
   🔹 Summary
-------------------------- */
function Summary({
  main_topic,
  emotional_status,
  relevant_behaviors,
  therapeutic_advances,
  risks,
  agreements,
}: {
  main_topic?: string;
  emotional_status?: string;
  relevant_behaviors?: string;
  therapeutic_advances?: string;
  risks?: string;
  agreements?: string;
}) {
  const { t } = useTranslation();
  const hasAnyField =
    main_topic ||
    emotional_status ||
    relevant_behaviors ||
    therapeutic_advances ||
    risks ||
    agreements;

  if (!hasAnyField) return null;

  return (
    <div className="group rounded-xl border border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/[0.02] p-6 mb-8 shadow-sm">
      <h3 className="mb-4 text-lg font-semibold text-content-primary">
        {t("session.detailedOverview")}
      </h3>

      <div className="space-y-4 text-sm text-gray-700 dark:text-gray-400">

        {main_topic && (
          <div>
            <h4 className="font-medium text-content-primary">{t("session.mainTopic")}</h4>
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(main_topic) }} />
          </div>
        )}

        {emotional_status && (
          <div>
            <h4 className="font-medium text-content-primary">{t("session.emotionalStatus")}</h4>
            <p
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(emotional_status),
              }}
            />
          </div>
        )}

        {relevant_behaviors && (
          <div>
            <h4 className="font-medium text-content-primary">{t("session.relevantBehaviors")}</h4>
            <p
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(relevant_behaviors),
              }}
            />
          </div>
        )}

        {therapeutic_advances && (
          <div>
            <h4 className="font-medium text-content-primary">{t("session.therapeuticAdvances")}</h4>
            <p
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(therapeutic_advances),
              }}
            />
          </div>
        )}

        {risks && (
          <div>
            <h4 className="font-medium text-content-primary">{t("session.detectedRisks")}</h4>
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(risks) }} />
          </div>
        )}

        {agreements && (
          <div>
            <h4 className="font-medium text-content-primary">{t("session.agreements.title")}</h4>
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(agreements) }} />
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------
   🔹 Related Sessions Table
-------------------------- */
import { RelatedSession } from "../../../domain/Session";
import { formatDate } from "../../../../../shared/utils/date";
// ...
function RelatedSessionsTable({ data }: { data: RelatedSession[] }) {
  const { t, i18n } = useTranslation();
  if (!data.length) return null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
      <h3 className="mb-4 text-lg font-semibold text-content-primary">
        {t("session.relatedSessionsTitle")}
      </h3>
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
        <Table>
          <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
            <TableRow>
              <TableCell className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400" isHeader>{t("session.date")}</TableCell>
              <TableCell className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400" isHeader>{t("session.title")}</TableCell>
              <TableCell className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400" isHeader>{t("session.similarity")}</TableCell>
              <TableCell className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400" isHeader>{t("session.therapyType")}</TableCell>
              <TableCell className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400" isHeader>{t("session.patient")}</TableCell>
            </TableRow>
          </TableHeader>
          <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
            {data.map((item, i) => (
              <TableRow key={i}>
                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                  <Link
                    to={`/session/${item.session_id}`}
                    className="text-accent-text hover:underline"
                  >
                    {item.date
                      ? formatDate(item.date, i18n.language)
                      : "—"}
                  </Link>
                </TableCell>
                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">{item.title}</TableCell>
                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">{new Intl.NumberFormat("es-ES", { style: "percent", minimumFractionDigits: 2 }).format(item.similarity)}</TableCell>
                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">{formatTherapyTypes(t, item.therapy_type)}</TableCell>
                <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                  <div>
                    <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">{item.patient_name}</span>
                    <span className="block text-gray-500 text-theme-xs dark:text-gray-400">{item.patient_last_name}</span>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* -------------------------
   🔹 Academic Articles Table
-------------------------- */
import { AcademicArticle } from "../../../domain/Session";
// ...
function AcademicArticlesTable({ data }: { data: AcademicArticle[] }) {
  const { t } = useTranslation();
  if (!data.length) return null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
      <h3 className="mb-4 text-lg font-semibold text-content-primary">
        {t("session.academicArticlesTitle")}
      </h3>

      <ul className="space-y-4">
        {data.map((recomendation) => (
          <li
            key={recomendation.id}
            className="p-4 border border-gray-200 rounded-xl dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/20 transition"
          >
            <h4 className="font-medium text-content-primary">
              {t("session.topic")}: {recomendation.title}
            </h4>
            <p className="text-sm text-content-secondary mt-1">
              {t("session.author")}: {recomendation.author}
            </p>

            <a
              href={recomendation.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 text-brand-500 hover:text-brand-600 text-sm"
            >
              {t("session.goToRecommendation")}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------
   🔹 Recommendations Table
-------------------------- */
import { Recommendation } from "../../../domain/Session";
import { formatTherapyTypes } from "../../../../../shared/domain/therapyTypes";
// ...
function RecommendationsTable({ data }: { data: Recommendation[] }) {
  const { t } = useTranslation();
  if (!data.length) return null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
      <h3 className="mb-4 text-lg font-semibold text-content-primary">
        {t("session.recommendationsTitle")}
      </h3>

      <ul className="space-y-4">
        {data.map((recomendation) => (
          <li
            key={recomendation.id}
            className="p-4 border border-gray-200 rounded-xl dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/20 transition"
          >
            <h4 className="font-medium text-content-primary">
              {recomendation.title}
            </h4>
            <p className="text-sm text-content-secondary mt-1">
              {recomendation.description}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
/* -------------------------
   🔹 Triggered Alerts List
-------------------------- */
function TriggeredAlertsList({ data }: { data: TriggeredAlert[] }) {
  const { t } = useTranslation();
  if (!data || data.length === 0) return null;

  const getAlertStyle = (criticality: string) => {
    const lower = criticality.toLowerCase();
    if (lower.includes("low") || lower.includes("baja")) {
      return {
        normalized: "low",
        border: "border-accent/20",
        bg: "bg-accent-tint",
        text: "text-accent-on-tint",
        badge: "bg-accent-tint text-accent-on-tint text-accent-text",
        icon: "text-accent-text"
      };
    }
    if (lower.includes("medium") || lower.includes("media") || lower.includes("yellow")) {
      return {
        normalized: "medium",
        border: "border-yellow-100 dark:border-yellow-900/30",
        bg: "bg-yellow-50 dark:bg-yellow-900/10",
        text: "text-yellow-800 dark:text-yellow-200",
        badge: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 text-accent-text",
        icon: "text-yellow-500"
      };
    }
    // Default to Red for High/Critical
    return {
      normalized: "critical",
      border: "border-red-100 dark:border-red-900/30",
      bg: "bg-red-50 dark:bg-red-900/10",
      text: "text-red-800 dark:text-red-200",
      badge: "bg-red-100 text-red-800 dark:bg-red-900/40 text-accent-text",
      icon: "text-red-500"
    };
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 dark:bg-white/[0.03] dark:border-gray-800">
      <h3 className="text-lg font-semibold text-content-primary mb-4 flex items-center">
        <AlertTriangle className="w-5 h-5 mr-2 text-gray-500" />
        {t("session.detectedAlerts")}
      </h3>
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.map((alert) => {
          const style = getAlertStyle(alert.criticality);
          return (
            <li key={alert.alert_id} className={`p-4 rounded-xl shadow-sm border ${style.border} ${style.bg}`}>
              <div className="flex justify-between items-start mb-2">
                <span className={`font-semibold ${style.text}`}>{alert.name}</span>
                <span className={`text-xs font-bold px-2 py-1 rounded-full uppercase ${style.badge}`}>
                  {t(`clinicalAlerts.modal.criticality.${style.normalized}`)}
                </span>
              </div>
              <p className="text-sm text-content-secondary leading-relaxed">{alert.context}</p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
